import { isCellInUse, type ClassifiableColumn, type SheetRow } from './sheet-row-classification'

/**
 * RF-06 do PRD 32 — a ação de coluna (menu `▾` do cabeçalho): "Aplicar à coluna"/"Limpar
 * coluna" para colunas comuns, marcação em massa para colunas de checkbox. Um item por
 * coluna, sempre — o rótulo e o estado (habilitado/desabilitado + motivo) são derivados
 * do estado atual das linhas em uso (RN-03), nunca de foco ou seleção.
 */

export type ColumnActionState =
  | { kind: 'disabled'; reason: string }
  | { kind: 'apply' }
  | { kind: 'clear' }
  | { kind: 'markAll' }
  | { kind: 'unmarkAll' }

const NO_ROWS_IN_USE_REASON = 'Nenhuma linha em uso — preencha ao menos uma linha da folha.'

/** RF-06 — a primeira célula preenchida da coluna, de cima para baixo, em qualquer linha. */
export function firstFilledValue(
  rows: SheetRow[],
  column: ClassifiableColumn,
  referenceDate: string
): unknown | null {
  for (const row of rows) {
    if (isCellInUse(row[column.key], column, referenceDate)) return row[column.key]
  }
  return null
}

/** RN-11 — motivo específico por tipo de coluna quando nenhuma célula está preenchida. */
function noFilledCellReason(column: ClassifiableColumn): string {
  if (column.type === 'date') return 'Digite uma data diferente da data do registro.'
  return 'Preencha ao menos uma célula desta coluna para replicar o valor.'
}

export function resolveColumnActionState(
  column: ClassifiableColumn,
  rows: SheetRow[],
  rowsInUseIndexes: number[],
  referenceDate: string
): ColumnActionState {
  if (rowsInUseIndexes.length === 0) {
    return { kind: 'disabled', reason: NO_ROWS_IN_USE_REASON }
  }

  if (column.type === 'checkbox') {
    const checkedCount = rowsInUseIndexes.filter((index) => rows[index][column.key] === true).length
    return checkedCount === rowsInUseIndexes.length ? { kind: 'unmarkAll' } : { kind: 'markAll' }
  }

  const filledCount = rowsInUseIndexes.filter((index) =>
    isCellInUse(rows[index][column.key], column, referenceDate)
  ).length

  if (filledCount === 0) return { kind: 'disabled', reason: noFilledCellReason(column) }
  return filledCount === rowsInUseIndexes.length ? { kind: 'clear' } : { kind: 'apply' }
}

/**
 * RF-06 — replica a primeira célula preenchida da coluna nas células vazias das linhas em
 * uso. Não depende de foco/seleção; nunca sobrescreve o que já está preenchido (RN-03); o
 * alcance é filtro sobre linhas já em uso, nunca coloca uma linha em uso (RF-06).
 */
export function applyToColumn(
  rows: SheetRow[],
  column: ClassifiableColumn,
  rowsInUseIndexes: number[],
  referenceDate: string
): SheetRow[] {
  const value = firstFilledValue(rows, column, referenceDate)
  if (value === null) return rows

  const inUse = new Set(rowsInUseIndexes)
  return rows.map((row, index) => {
    if (!inUse.has(index)) return row
    if (isCellInUse(row[column.key], column, referenceDate)) return row
    return { ...row, [column.key]: value }
  })
}

/**
 * RF-06 — esvazia as células da coluna nas linhas em uso; em coluna de data, "limpar"
 * restaura a `referenceDate` em vez de esvaziar. As vazias nunca são afetadas.
 */
export function clearColumn(
  rows: SheetRow[],
  column: ClassifiableColumn,
  rowsInUseIndexes: number[],
  referenceDate: string
): SheetRow[] {
  const emptyValue =
    column.type === 'date' ? referenceDate : column.type === 'checkbox' ? false : ''
  const inUse = new Set(rowsInUseIndexes)

  return rows.map((row, index) => {
    if (!inUse.has(index)) return row
    if (!isCellInUse(row[column.key], column, referenceDate)) return row
    return { ...row, [column.key]: emptyValue }
  })
}

/** RF-06 — marcação em massa da coluna de checkbox, nas linhas em uso. */
export function setColumnCheckbox(
  rows: SheetRow[],
  column: ClassifiableColumn,
  rowsInUseIndexes: number[],
  checked: boolean
): SheetRow[] {
  const inUse = new Set(rowsInUseIndexes)
  return rows.map((row, index) => (inUse.has(index) ? { ...row, [column.key]: checked } : row))
}
